import {
  boardToScreen,
  nanoid,
  type CommentMessage,
  type CommentNode,
  type Vec,
} from '@kanvas/board-core';
import { useMemo, useState } from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { useDocVersion, useNodes } from '../collab/hooks';
import type { Identity } from '../lib/identity';
import { useEditor } from '../state/store';
import { Icon } from '../ui/icons';

export interface CommentsLayerProps {
  doc: BoardDoc;
  identity: Identity;
}

/**
 * Comment pins and threads.
 *
 * The pin is a document node (so it moves with the board and survives reload),
 * while the messages live in a separate Y.Array. Keeping them apart means a
 * long discussion never rewrites the pin, and the board can render thousands of
 * pins without loading their bodies.
 */
export function CommentsLayer({ doc, identity }: CommentsLayerProps) {
  const nodes = useNodes(doc);
  const version = useDocVersion(doc);
  const viewport = useEditor((s) => s.viewport);
  const [openThread, setOpenThread] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const pins = useMemo(
    () => nodes.filter((n): n is CommentNode => n.type === 'comment'),
    [nodes],
  );

  const messages = useMemo(() => {
    const byThread = new Map<string, CommentMessage[]>();
    for (const message of doc.comments.toArray()) {
      const list = byThread.get(message.threadId) ?? [];
      list.push(message);
      byThread.set(message.threadId, list);
    }
    return byThread;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, version]);

  if (pins.length === 0) return null;

  const canComment = doc.canComment();

  const submit = (threadId: string) => {
    if (!canComment) return;
    const body = draft.trim();
    if (body === '') return;
    doc.transact(() => {
      doc.comments.push([
        {
          id: nanoid(12),
          threadId,
          authorId: identity.userId,
          authorName: identity.name,
          body,
          createdAt: Date.now(),
          editedAt: null,
        },
      ]);
    });
    setDraft('');
  };

  return (
    <div className="comments-layer">
      {pins.map((pin) => {
        const p = boardToScreen(viewport, { x: pin.x, y: pin.y });
        if (p.x < -40 || p.y < -40 || p.x > viewport.width + 40 || p.y > viewport.height + 40) {
          return null;
        }
        const thread = messages.get(pin.threadId) ?? [];
        const open = openThread === pin.threadId;

        return (
          <div key={pin.id} className="comment-anchor" style={{ left: p.x, top: p.y }}>
            <button
              type="button"
              className={`comment-pin ${pin.resolved ? 'is-resolved' : ''}`}
              title={thread.length > 0 ? `${thread.length} комментариев` : 'Пустой комментарий'}
              onClick={() => {
                setOpenThread(open ? null : pin.threadId);
                setDraft('');
              }}
            >
              {thread.length > 0 ? thread.length : <Icon name="comment" size={13} />}
            </button>

            {open && (
              <div className="comment-thread" onPointerDown={(e) => e.stopPropagation()}>
                <header>
                  <span>Обсуждение</span>
                  <div className="comment-thread-actions">
                    {canComment && (
                      <button
                        type="button"
                        title={pin.resolved ? 'Открыть снова' : 'Решено'}
                        onClick={() => doc.update(pin.id, { resolved: !pin.resolved })}
                      >
                        <Icon name="check" size={14} />
                      </button>
                    )}
                    {canComment && (
                      <button
                        type="button"
                        title="Удалить обсуждение"
                        onClick={() => {
                          doc.remove([pin.id]);
                          setOpenThread(null);
                        }}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    )}
                    <button type="button" title="Закрыть" onClick={() => setOpenThread(null)}>
                      <Icon name="close" size={14} />
                    </button>
                  </div>
                </header>

                <div className="comment-messages">
                  {thread.length === 0 && <p className="comment-empty">Пока нет комментариев.</p>}
                  {thread.map((message) => (
                    <article key={message.id}>
                      <strong>{message.authorName}</strong>
                      <time>{formatTime(message.createdAt)}</time>
                      <p>{message.body}</p>
                    </article>
                  ))}
                </div>

                {canComment ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit(pin.threadId);
                  }}
                >
                  <textarea
                    value={draft}
                    placeholder="Напишите комментарий…"
                    autoFocus
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submit(pin.threadId);
                      }
                    }}
                  />
                  <button type="submit" className="primary-btn" disabled={draft.trim() === ''}>
                    Отправить
                  </button>
                </form>
                ) : (
                  <p className="comment-empty">Только просмотр — комментировать нельзя.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'только что';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} мин назад`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} ч назад`;
  return new Date(ts).toLocaleDateString();
}

/** Creates a pin at a board point; the thread starts empty. */
export function createCommentPin(
  doc: BoardDoc,
  identity: Identity,
  at: Vec,
  threadId: string,
): CommentNode {
  const now = Date.now();
  const node: CommentNode = {
    id: `n_${nanoid(14)}`,
    type: 'comment',
    x: at.x,
    y: at.y,
    w: 22,
    h: 22,
    rotation: 0,
    index: doc.topIndex(),
    parentId: null,
    opacity: 1,
    locked: false,
    createdBy: identity.userId,
    createdAt: now,
    updatedAt: now,
    threadId,
    resolved: false,
    anchorNodeId: null,
  };
  doc.add(node);
  return node;
}
